import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { LetsikringAfIndtaegt } from '@features/letregister/letsikring-af-indtaegt.model';
import { LetsikringRevision } from '@features/letregister/letsikring-revision.model';
import { LetsikringRisiko } from '@features/letregister/letsikring-risiko.model';
import { Letsikring } from '@features/letregister/letsikring.model';
import { LetsikringAfIndtaegtComponent } from '../letsikring-af-indtaegt/letsikring-af-indtaegt.component';
import { LetsikringRisikoComponent } from '../letsikring-risiko/letsikring-risiko.component';
import LetsikringStamoplysningerComponent from '../letsikring-stamoplysninger/letsikring-stamoplysninger.component';
import { LetsikringSynkroniseringComponent } from '../letsikring-synkronisering/letsikring-synkronisering.component';
import { LetsikringSystemComponent } from '../letsikring-system/letsikring-system.component';
import { LetsikringTilSelvstaendigeComponent } from '../letsikring-til-selvstaendige/letsikring-til-selvstaendige.component';
import { LetregisterService } from '@features/letregister/letregister.service';

@Component({
  selector: 'app-letsikring-revision',
  imports: [
    LetsikringTilSelvstaendigeComponent,
    LetsikringAfIndtaegtComponent,
    LetsikringRisikoComponent,
    LetsikringSynkroniseringComponent,
    LetsikringSystemComponent,
    LetsikringStamoplysningerComponent,
  ],
  templateUrl: './letsikring-revision.component.html',
  styleUrl: './letsikring-revision.component.css',
})
export class LetsikringRevisionComponent {
  fb = inject(FormBuilder);
  letregisterService = inject(LetregisterService);

  editMode = input.required<boolean>();
  revision = input.required<LetsikringRevision>();
  letsikring = input.required<Letsikring>();

  selectedLivrente = signal<LetsikringAfIndtaegt | undefined>(undefined);
  selectedRisiko = signal<LetsikringRisiko | undefined>(undefined);

  constructor() {
    effect(() => {
      const rev = this.revision();

      if (rev.letsikringAfIndtaegt) {
        this.selectedLivrente.set(rev.letsikringAfIndtaegt);
        this.selectedRisiko.set(undefined);
      }
      if (rev.letsikringRisiko) {
        this.selectedLivrente.set(undefined);
        this.selectedRisiko.set(rev.letsikringRisiko);
      }
    });

    effect(() => {
      const letsikring = this.letsikring();
      untracked(() => {
        this.resetForm(letsikring);
      });
    });
  }

  form = this.fb.group({
    itcLetsikringId: undefined,
    eksternReference: undefined,
    opsparetVaerdi: this.fb.group({
      indbetaltAarTilDato: undefined,
      opsparetVaerdi: undefined,
      opsparetVaerdiKundeKapital: undefined,
    }),
    gaeldendeRevision: this.fb.group({
      kommentar: ['', { validators: [Validators.required] }],
      ikrafttraedelse: ['', { validators: [Validators.required] }],
      haendelsesKode: [0, { validators: [Validators.required] }],
      synkroniseringStatus: undefined,
      letsikringRisiko: this.fb.group({
        opsigelsesDato: undefined,
        pantsatType: undefined,
        straksopkraevningValgt: undefined,
        straksopkraevning: this.fb.group({
          opkraevningsBeloeb: undefined,
          periodeFra: undefined,
          periodeTil: undefined,
          kommentar: undefined,
        }),
        debiteringskonto: this.fb.group({
          kontoNummer: undefined,
          regNr: undefined,
          navn: undefined,
          skyggesaldoType: undefined,
        }),
        forsikredeId: undefined,
        forsikredeNavn: undefined,
        barnId: undefined,
        barnNavn: undefined,
        betalingViaFrieMidler: undefined,
        aarligIndkomst: undefined,
        underUdbetaling: undefined,
        letsikringRisikoProduktValg: this.fb.group({
          frekvensForIndbetaling: undefined,
          skattekode: undefined,
          daekningsstoerrelse: undefined,
        }),
        skattekode: undefined,
        gruppeProduktId: undefined,
        _pris: undefined,
        _ydelse: undefined,
      }),
      letsikringAfIndtaegt: this.fb.group({
        oprettelsesDato: undefined,
        tilstand: undefined,
        stopAarsag: undefined,
        unisex: undefined,
        pensionsreform: undefined,
        tegningsmaade: undefined,
        arbejdsgiverCvr: undefined,
        arbejdsgiverNavn: undefined,
        selvstaendigErhvervsdrivendeOrdning: undefined,
        indbetaling: this.fb.group({
          iBero: undefined,
          indskud: undefined,
          indskudPrivatBeloeb: undefined,
          indskudMedarbejderBeloeb: undefined,
          indskudArbejdsgiverBeloeb: undefined,
          indskudDato: undefined,
          indskudskonto: this.fb.group({
            kontoNummer: undefined,
            regNr: undefined,
            navn: undefined,
            skyggesaldoType: undefined,
          }),
          loebendeIndbetaling: undefined,
          loebendeNaesteIndbetalingDato: undefined,
          loebendeIndbetalingFrekvens: undefined,
          loebendeSidsteIndbetalingDato: undefined,
          loebendeIndbetalingLaengstMulig: undefined,
          loebendeIndbetalingIprocent: undefined,
          loebendeArbejdsgiverIndbetalingPct: undefined,
          loebendeMedarbejderIndbetalingPct: undefined,
          pensionsgivendeLoen: undefined,
          loebendeArbejdsgiverIndbetalingBeloeb: undefined,
          loebendeMedarbejderIndbetalingBeloeb: undefined,
          loebendePrivatIndbetalingBeloeb: undefined,
          loebendeIndbetalingKonto: this.fb.group({
            kontoNummer: undefined,
            regNr: undefined,
            navn: undefined,
            skyggesaldoType: undefined,
          }),
          rabatVederlagLoebendeIndbetalingerProcent: undefined,
          rabatVederlagEngangIndbetalingerProcent: undefined,
        }),
        overloebFraRatepension: undefined,
        aarligIndbetaling: undefined,
        rateOverloebsgraense: undefined,
        overloebsRateKonto: this.fb.group({
          kontoNummer: undefined,
          regNr: undefined,
          navn: undefined,
          skyggesaldoType: undefined,
        }),
        underUdbetaling: undefined,
        udbetalingStart: undefined,
        tilbagekoebValgt: undefined,
        tilbagekoeb: this.fb.group({
          nemkonto: undefined,
          tilbagekoebDato: undefined,
          tilbagekoebKonto: this.fb.group({
            kontoNummer: undefined,
            regNr: undefined,
            navn: undefined,
            skyggesaldoType: undefined,
          }),
        }),
        udbetaling: this.fb.group({
          nemkonto: undefined,
          udbetalingskonto: this.fb.group({
            kontoNummer: undefined,
            regNr: undefined,
            navn: undefined,
            skyggesaldoType: undefined,
          }),
          skatteKortType: undefined,
          konverterTilEngangsudbetaling: undefined,
        }),
        simUdbetalingStart: undefined,
        simGarantiPeriode: undefined,
        praemiefritagelse: undefined,
        depotsikring: undefined,
        naermestePaaroerendeBegunstigelse: undefined,
        depotsikringIndbetalingstype: undefined,
        depotsikringSkattekode: undefined,
        frieMidler: undefined,
        depotsikringBetalingsKonto: this.fb.group({
          kontoNummer: undefined,
          regNr: undefined,
          navn: undefined,
          skyggesaldoType: undefined,
        }),
        garanti: undefined,
        garantiPeriode: undefined,
        garantiMaks: undefined,
        medforsikredeId: undefined,
        medforsikredeNavn: undefined,
        investeringsprofil: undefined,
        investeringsandelHoejRisiko: undefined,
        udbetalingssikring: undefined,
        kundeKapital: undefined,
        klimaValgt: undefined,
        investeringsandelKlimaplus: undefined,
        rabatVederlagOverfoerslerProcent: undefined,
        saldoRabat: undefined,
      }),
      letsikringTilSelvstaendige: this.fb.group({
        cvrNr: undefined,
        firmanavn: undefined,
        firmaType: undefined,
        firmaRelationsType: undefined,
        arbejdsgiverBidrag: undefined,
        medarbejderBidrag: undefined,
        privatBidrag: undefined,
        tegningsmaade: undefined,
      }),
    }),
  });

  async resetForm(letsikring?: Letsikring) {
    if (letsikring) {
      this.form.reset();
      this.form.patchValue(letsikring);

      const opsparetVaerdi =
        await this.letregisterService.opsparetVaerdi(letsikring);

      this.form.patchValue({
        opsparetVaerdi: opsparetVaerdi,
        gaeldendeRevision: {
          kommentar: undefined,
          letsikringRisiko: {
            forsikredeId:
              letsikring.gaeldendeRevision.letsikringRisiko?.forsikrede?.id,
            forsikredeNavn:
              letsikring.gaeldendeRevision.letsikringRisiko?.forsikrede?.navn,
            barnId: letsikring.gaeldendeRevision.letsikringRisiko?.barn?.id,
            barnNavn: letsikring.gaeldendeRevision.letsikringRisiko?.barn?.navn,
            _pris: this.letregisterService.getGruppeProduktAarsData(
              letsikring.gaeldendeRevision.letsikringRisiko
            )?.aarligPris,
            _ydelse: this.letregisterService.getGruppeProduktAarsData(
              letsikring.gaeldendeRevision.letsikringRisiko
            )?.ydelse,
          },
          letsikringAfIndtaegt: {
            medforsikredeId:
              letsikring.gaeldendeRevision.letsikringAfIndtaegt?.medforsikrede
                ?.id,
            medforsikredeNavn:
              letsikring.gaeldendeRevision.letsikringAfIndtaegt?.medforsikrede
                ?.navn,
            tilbagekoebValgt:
              !!letsikring.gaeldendeRevision.letsikringAfIndtaegt?.tilbagekoeb,
          },
        },
      });
    }
  }

  onSelectLivrente() {
    this.selectedLivrente.set(this.revision().letsikringAfIndtaegt);
    this.selectedRisiko.set(undefined);
    this.form.controls.gaeldendeRevision.controls.letsikringRisiko.reset();
  }

  onSelectTilknyttet(letsikringRisiko: LetsikringRisiko) {
    this.selectedLivrente.set(undefined);
    this.selectedRisiko.set(letsikringRisiko);
    this.form.controls.gaeldendeRevision.controls.letsikringRisiko.patchValue(
      letsikringRisiko
    );
  }

  getProduktNavn = computed(() => {
    let navn = '??';

    if (this.selectedRisiko()) {
      navn = this.selectedRisiko()!.produktNavn || '?';
    }

    if (this.selectedLivrente()) {
      navn = 'Letsikring af indtægt';
      if (this.selectedLivrente()?.letsikringTilSelvstaendige) {
        navn += ' (LTS)';
      }
    }

    return navn;
  });
}
